import { toast } from "sonner";
import { Twitter, Facebook, Linkedin, Mail, Link2 } from "lucide-react";

/**
 * ShareBar — compact, tasteful social share controls for an article.
 * Uses the canonical article URL so shared cards resolve to the live domain
 * (with the signature australia-water.jpg OG image as the default preview).
 */
export default function ShareBar({
  title,
  url,
  label = "Share this article",
  testIdPrefix = "share",
}) {
  const enc = encodeURIComponent;
  const shares = [
    {
      key: "x",
      label: "Share on X",
      href: `https://twitter.com/intent/tweet?url=${enc(url)}&text=${enc(title)}`,
      Icon: Twitter,
    },
    {
      key: "facebook",
      label: "Share on Facebook",
      href: `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`,
      Icon: Facebook,
    },
    {
      key: "linkedin",
      label: "Share on LinkedIn",
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}`,
      Icon: Linkedin,
    },
    {
      key: "email",
      label: "Share by email",
      href: `mailto:?subject=${enc(title)}&body=${enc(`${title}\n\n${url}`)}`,
      Icon: Mail,
    },
  ];

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied to clipboard");
    } catch {
      toast.error("Could not copy the link");
    }
  };

  const btn =
    "h-10 w-10 rounded-full border border-sand-300 bg-white/70 text-forest-700 flex items-center justify-center transition-all duration-300 ease-out hover:-translate-y-0.5 hover:text-clay-600 hover:border-clay-300 hover:shadow-soft";

  return (
    <div
      className="flex items-center gap-4 flex-wrap"
      data-testid={`${testIdPrefix}-bar`}
    >
      <span className="overline">{label}</span>
      <div className="flex items-center gap-2">
        {shares.map(({ key, label: l, href, Icon }) => (
          <a
            key={key}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={l}
            className={btn}
            data-testid={`${testIdPrefix}-${key}`}
          >
            <Icon size={17} strokeWidth={1.6} />
          </a>
        ))}
        <button
          type="button"
          onClick={copyLink}
          aria-label="Copy link"
          className={btn}
          data-testid={`${testIdPrefix}-copy`}
        >
          <Link2 size={17} strokeWidth={1.6} />
        </button>
      </div>
    </div>
  );
}
